import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BackpackModule } from 'app/backpack/backpack.module';
import { DialogContentComponent } from 'app/backpack/dialog-modal/dialog-content.component';
import { DialogService } from 'app/backpack/dialog-modal/dialog.service';
import { ResolveAssetPipe } from 'app/backpack/resolve-asset.pipe';
import { ProTrialService } from 'app/services/pro-trial.service';
import { take } from 'rxjs';

// Pro Upsell Cleanup Marker
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [ResolveAssetPipe, BackpackModule, CommonModule],
	selector: 'app-upsell-modal',
	template: `
		<ng-container></ng-container>
		<ng-template #dialogContent let-dismiss>
			<bp-dialog-header class="dialog-header">
				<div class="modal-header-content">
					<img [src]="'./assets/upsell.svg' | resolveAsset" [width]="298" [height]="230" />
					<div *ngIf="type === 'intro'" class="modal-title">You've Been Upgraded!</div>
					<div *ngIf="type === 'reminder'" class="modal-title">Your Upgrade is Ending Soon</div>
				</div>
			</bp-dialog-header>

			<bp-dialog-body>
				<div class="modal-body-content">
					<span *ngIf="type === 'intro'">
						You now have access to
						<sp-tag [type]="'secondary'" [style]="tagStyle" class="pro-tag"> <bp-icon size="12" [icon]="'sparkles'"></bp-icon> PRO </sp-tag>
						features through&nbsp;
						<div class="date-text">{{ endDate | date : 'MMMM d' : 'UTC' }}</div>
						.
					</span>
					<span *ngIf="type === 'reminder'">
						Your school's
						<sp-tag [type]="'secondary'" [style]="tagStyle" class="pro-tag"> <bp-icon size="12" [icon]="'sparkles'"></bp-icon> pro </sp-tag>
						free trial is ending soon:&nbsp;
						<div class="date-text">{{ daysLeftText }}</div>
						.
					</span>
				</div>
				<hr class="modal-separator" />
				<div class="info-row">
					<div class="icon-badge" style="background: linear-gradient(143deg, #F86F4D -34.92%, #F77454 73.22%);">
						<bp-icon size="24" [icon]="'clock-03'" [style.color]="'white'"></bp-icon>
					</div>
					<div class="info-text">
						<div class="subheader">Missed Class Time</div>
						<div class="info-detail">{{ infoMCT }}</div>
						<a [href]="learnMoreMCT" target="_blank" class="learn-more">Learn more</a>
					</div>
				</div>
				<div class="info-row">
					<div class="icon-badge" style="background: radial-gradient(125.28% 125.28% at 10.99% 0%, #2F287A 0%, #1F195E 100%);">
						<bp-icon size="24" [icon]="'detected-encounters'" [style.color]="'white'"></bp-icon>
					</div>
					<div class="info-text">
						<div class="subheader">Detection Encounters</div>
						<div class="info-detail">{{ infoDE }}</div>
						<a [href]="learnMoreDE" target="_blank" class="learn-more">Learn more</a>
					</div>
				</div>
				<div class="info-row">
					<div class="icon-badge" style="background: linear-gradient(139deg, #E32C66 1.57%, #CB1B53 100%);">
						<bp-icon size="24" [icon]="'emergency'" [style.color]="'white'"></bp-icon>
					</div>
					<div class="info-text">
						<div class="subheader">Emergency Mode</div>
						<div class="info-detail">{{ infoEM }}</div>
						<a [href]="learnMoreEM" target="_blank" class="learn-more">Learn more</a>
					</div>
				</div>
			</bp-dialog-body>
			<bp-dialog-footer>
				<bp-button (click)="needHelp()" type="white" [title]="type == 'intro' ? 'Have Questions?' : 'Need Help?'"></bp-button>
				<bp-button (click)="gotIt(dismiss)" type="color" title="Got it!"></bp-button>
			</bp-dialog-footer>
		</ng-template>
	`,
	styles: [
		`
			.dialog-header {
				padding-bottom: 8px;
			}

			.modal-header-content {
				display: flex;
				flex-direction: column;
				align-items: center;
				text-align: center;
				padding-left: 40px;
			}

			.modal-title {
				padding-top: 32px;
			}

			.modal-body-content {
				display: flex;
				flex-direction: column;
				align-items: center;
				color: #1f195e;
			}

			.date-text {
				color: #1f195e;
				font-weight: 600;
			}

			.modal-body-content span {
				display: flex;
				flex-wrap: nowrap;
				align-items: center;
				white-space: nowrap;
			}

			.pro-tag {
				padding: 0 4px 0 4px;
			}

			.modal-separator {
				background: #f0f2f5;
				border: none;
				height: 1px;
				margin: 24px 0 24px 0;
			}

			.icon-badge {
				border-radius: 8px;
				padding: 4px;
				width: 36px;
				height: 36px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
			}

			.info-row {
				display: flex;
				align-items: flex-start;
				gap: 12px;
				margin-bottom: 28px;
			}

			.info-text {
				display: flex;
				flex-direction: column;
				justify-content: center;
			}

			.subheader {
				color: #1f195e;
				font-family: Inter, sans-serif;
				font-size: 14px;
				font-style: normal;
				font-weight: 700;
				line-height: normal;
				margin-bottom: 4px;
			}

			.info-detail {
				color: #4e5d74;
				font-family: Inter, sans-serif;
				font-size: 12px;
				font-style: normal;
				font-weight: 400;
				line-height: normal;
				margin-bottom: 4px;
			}

			.learn-more {
				color: #4e5d74;
				font-family: Inter, sans-serif;
				font-size: 12px;
				font-style: normal;
				font-weight: 500;
				line-height: normal;
				text-decoration-line: underline;
				text-decoration-style: solid;
				text-decoration-skip-ink: none;
				text-decoration-thickness: auto;
				text-underline-offset: auto;
				text-underline-position: from-font;
				cursor: pointer;
			}
		`,
	],
})
export class UpsellModalComponent implements AfterViewInit {
	@ViewChild('dialogContent', { read: TemplateRef, static: false }) dialogContent!: TemplateRef<unknown>;
	@Input() type: 'intro' | 'reminder' = 'intro';

	endDate = this.proTrialService.disableAfterDate;

	learnMoreMCT = 'https://articles.smartpass.app/en/articles/8236966-missed-class-time';
	learnMoreDE = 'https://articles.smartpass.app/en/articles/6855964-detected-encounters';
	learnMoreEM = 'https://articles.smartpass.app/en/articles/8201659-emergency-mode';
	infoMCT = 'See which classes students are missing the most as they use digital hall passes. Teachers love this data for classroom accountability!';
	infoDE = 'Get ahead of problems. Discover which students could be meeting up—and then automatically block future attempts.';
	infoEM =
		'Every minute counts. Immediately pause all student movement, quickly identify who’s in the halls, and let any nearby teacher check them in.';

	tagStyle = { fontColor: 'tw-text-peach-500', backgroundColor: 'tw-bg-peach-100' };

	constructor(public backpackDialogService: DialogService, public cdr: ChangeDetectorRef, private proTrialService: ProTrialService) {}

	get daysLeftText(): string {
		const today = new Date();
		const msPerDay = 1000 * 60 * 60 * 24;
		const daysLeft = Math.max(0, Math.ceil((this.endDate.getTime() - today.getTime()) / msPerDay));
		const text = daysLeft === 1 ? 'day left' : 'days left';
		return `${daysLeft} ${text}`;
	}

	needHelp() {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window.Intercom('showNewMessage');
	}

	gotIt(dismiss: () => void) {
		dismiss();
	}

	ngAfterViewInit(): void {
		const dialogRef: MatDialogRef<DialogContentComponent, unknown> = this.backpackDialogService.openDialog(this.dialogContent, this.cdr);
		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				if (this.type === 'intro') {
					this.proTrialService.markIntroDialogAsSeen();
				} else {
					this.proTrialService.markReminderDialogAsSeen();
				}
			});
	}
}
